import React, { useState, useEffect, useRef } from "react";

import { globalAlert, globalLoader } from "../../actions/commonActions";
import { UPLOAD_IMAGE } from "../../actionTypes/types";
import InputComponent from "../_common/inputComponent";
import LmsOrgFields from "./lmsOrgFields";
import ConfirmationModal from "../_common/confirmModal";
import { useTranslation } from "react-i18next";

const EditBrandInfo = (props) => {
  const { t } = useTranslation();
  const [orgName, setOrgName] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [physicalCardEnabled, setPhysicalCardEnabled] = useState(false);

  const [logo, setLogo] = useState("");
  const [logoImg, setLogoImg] = useState("");
  const [orgNameErr, setOrgNameErr] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const [logoFile, setLogoFile] = useState("");
  const [copyrightText, setCopyrightText] = useState("");

  // Learning feature — managed by the shared LmsOrgFields component.
  const [lmsState, setLmsState] = useState({
    lmsEnabled: false,
    onlyLmsEnabled: false,
    lmsShortName: "",
    domains: [],
    shortNameValid: false,
  });
  const [lmsErr, setLmsErr] = useState("");
  const [confirm, setConfirm] = useState(null); // { description, fd }
  const prevShort = useRef("");

  useEffect(() => {
    setLmsErr(props?.lmsServerError || "");
  }, [props?.lmsServerError]);

  const onLmsChange = (s) => {
    if (s.lmsShortName !== prevShort.current) {
      setLmsErr("");
      prevShort.current = s.lmsShortName;
    }
    setLmsState(s);
  };

  useEffect(() => {
    setPropsToState(); // eslint-disable-next-line
  }, [props?.data]);

  const setPropsToState = () => {
    setOrgName(props?.data?.orgName);
    setLogoImg(props?.data?.brandLogo);
    setCopyrightText(props?.data?.copyrightText);
    setIsActive(props?.data?.isAdminMfaEnabled || false)
    setPhysicalCardEnabled(props?.data?.isPhysicalCardEnabled || false)
    // setCreateDate(props?.data?.created);
  };

  const doSubmit = (formData) => {
    globalLoader(true);
    props.valueSubmit(formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await handleValidation())) return;

    let formData = new FormData();
    formData.append("copyrightText", copyrightText);
    formData.append("orgName", orgName);
    formData.append("file", logo);
    formData.append("isAdminMfaEnabled", isActive);
    formData.append("isPhysicalCardEnabled", physicalCardEnabled);

    // Learning feature — partial update: send each field only when it changed,
    // so untouched values are preserved server-side.
    const initEnabled = !!props?.data?.lmsEnabled;
    const initOnlyLmsEnabled = !!props?.data?.onlyLmsEnabled;
    const initShort = props?.data?.lmsShortName || "";

    if (lmsState.lmsEnabled !== initEnabled) {
      formData.append("lmsEnabled", String(lmsState.lmsEnabled));
    }
    if (lmsState.onlyLmsEnabled !== initOnlyLmsEnabled) {
      formData.append("onlyLmsEnabled", String(lmsState.onlyLmsEnabled));
    }
    if (lmsState.lmsEnabled && lmsState.lmsShortName !== initShort) {
      formData.append("lmsShortName", lmsState.lmsShortName);
    }

    // Confirm before actions the learner side cares about: disabling makes the
    // learner portal unavailable; changing the short name breaks shared links.
    const disabling = initEnabled && !lmsState.lmsEnabled;
    const shortNameChanged =
      lmsState.lmsEnabled && initEnabled && lmsState.lmsShortName !== initShort;
    if (disabling) {
      setConfirm({
        description: t(
          "superadmin.lms.confirmDisable",
          "Disabling the e-learning platform will make the learner portal unavailable. The Short Name will remain reserved. Continue?"
        ),
        fd: formData,
      });
      return;
    }
    if (shortNameChanged) {
      setConfirm({
        description: t(
          "superadmin.lms.warning",
          "Changing the Short Name will update the learner frontend URL. Existing shared links may stop working."
        ),
        fd: formData,
      });
      return;
    }
    doSubmit(formData);
  };

  const handleValidation = () => {
    let flag = true;
    if (!orgName) {
      setOrgNameErr(true);
      flag = false;
    }

    if (!logoImg) {
      setLogoErr(true);
      flag = false;
    }
    // Short Name required + valid only when the Learning feature is enabled.
    if (lmsState.lmsEnabled) {
      if (!lmsState.lmsShortName) {
        setLmsErr(
          t(
            "superadmin.lms.shortNameRequired",
            "Short Name is mandatory when the Learning feature is enabled."
          )
        );
        flag = false;
      } else if (!lmsState.shortNameValid) {
        setLmsErr(
          t(
            "superadmin.lms.shortNameInvalid",
            "Use 3–30 lowercase letters, numbers or hyphens; no spaces and no leading/trailing hyphen."
          )
        );
        flag = false;
      }
    }
    return flag;
  };

  const setLogoFunc = (ev) => {
    if (
      ev?.target?.files[0]?.type === "image/jpeg" ||
      ev?.target?.files[0]?.type === "image/png"
    ) {
      const file = ev.target.files[0];
      setLogo(file);
      const url = URL.createObjectURL(file);
      setLogoFile(url);
    } else {
      globalAlert("error", t("superadmin.editBrandInfo.onlyPngJpegAllowed"));
    }
  };
  return (
    <>
      <div className="col-md-6 px-0 mx-auto pb-2">
        <form onSubmit={handleSubmit} className="form-own px-3">
          <div className="row px-2">
            {props?.data?.areaCode?.length > 0 && props?.edit && (
              <div className="col-12 px-2">
                <div className="form-own-group">
                  <label htmlFor="postCode" className="form-own-label">
                    {t("superadmin.editBrandInfo.currentAreacodeList")}
                  </label>
                  <ul className="list-unstyled arecode-list">
                    {props.data.areaCode.map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="col-6 px-2">
              <div className="form-own-group">
                <InputComponent
                  placeholder={t("superadmin.editBrandInfo.namePlaceholder")}
                  label={t("superadmin.editBrandInfo.nameLabel")}
                  type="text"
                  name="name"
                  theme="admin"
                  value={orgName}
                  handleCallback={(val) => {
                    setOrgName(val);
                  }}
                />
                <div className="error-text">
                  {!orgName && orgNameErr && (
                    <span>{t("superadmin.editBrandInfo.orgNameRequired")}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-6 px-2">
              <div className="form-own-group">
                {/* <label htmlFor="postCode" className="form-own-label">
                  Copyright text
                </label>
                <input
                  id="postCode"
                  name="postCode"
                  placeholder="Copyright text"
                  className="form-control"
                  onChange={(evt) => {
                    setCopyrightText(evt.target.value);
                  }}
                  value={copyrightText}
                  maxLength="50"
                /> */}
                <InputComponent
                  placeholder={t("superadmin.editBrandInfo.copyrightTextPlaceholder")}
                  label={t("superadmin.editBrandInfo.copyrightTextLabel")}
                  type="text"
                  name="Copyright text"
                  theme="admin"
                  value={copyrightText}
                  handleCallback={(val) => {
                    setCopyrightText(val);
                  }}
                />
              </div>
            </div>

            <div className="form-own-group toggle-row px-2">
              <label htmlFor="switch" className="form-own-label mb-0">
                {t("superadmin.editBrandInfo.isAdminMfaRequired")}
              </label>
              <input
                className="toggle-input"
                type="checkbox"
                id="switch"
                onChange={(ev) => {
                  setIsActive(ev.target.checked);
                }}
                checked={isActive}
              />
              <label className="toggle-label" htmlFor="switch">
                {t("superadmin.editBrandInfo.toggleLabel")}
              </label>
            </div>
            <div className="form-own-group toggle-row px-2">
              <label htmlFor="switchPhysicalCard" className="form-own-label mb-0">
                {t("superadmin.editBrandInfo.isPhysicalCardEnabled")}
              </label>
              <input
                className="toggle-input"
                type="checkbox"
                id="switchPhysicalCard"
                data-testid="pc-toggle"
                onChange={(ev) => {
                  setPhysicalCardEnabled(ev.target.checked);
                }}
                checked={physicalCardEnabled}
              />
              <label className="toggle-label" htmlFor="switchPhysicalCard">
                {t("superadmin.editBrandInfo.toggleLabel")}
              </label>
            </div>

            <LmsOrgFields
              initial={{
                lmsEnabled: props?.data?.lmsEnabled,
                onlyLmsEnabled: props?.data?.onlyLmsEnabled,
                lmsShortName: props?.data?.lmsShortName,
              }}
              serverShortNameError={lmsErr}
              theme="admin"
              onChange={onLmsChange}
            />

            <div className="col-12 px-2">
              <div className="form-own-group">
                <label className="form-own-label">{t("superadmin.editBrandInfo.uploadLogoLabel")}</label>
                <div className="upload-btn upload-btn-dashed" id="previewLogo">
                  <div className="show-icon-wrapper pb-2 mb-1">
                    <img
                      alt="preview logo"
                      id="previewLogo"
                      src={
                        logoFile ? logoFile : logoImg ? logoImg : UPLOAD_IMAGE
                      }
                    />
                  </div>

                  <div className="upload-btn-txt btn">
                    + {logoImg || logoFile ? t("superadmin.editBrandInfo.changeFile") : t("superadmin.editBrandInfo.addFileText")} {t("superadmin.editBrandInfo.fileLabel")}
                  </div>
                  <input
                    type="file"
                    accept="image/x-png,image/jpeg"
                    className="upload-input cursor"
                    onChange={setLogoFunc}
                  />
                </div>
                <div className="error-text">
                  {!logoFile && !logoImg && logoErr && (
                    <span>{t("superadmin.editBrandInfo.logoRequired")}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="admin-btn">
            <button type="submit" className="btn primary-btn ml-2">
              {t("common.save")}
            </button>
          </div>
        </form>
      </div>

      {confirm && (
        <ConfirmationModal
          open={!!confirm}
          description={confirm.description}
          onCloseModal={(ok) => {
            const pending = confirm;
            setConfirm(null);
            if (ok && pending?.fd) doSubmit(pending.fd);
          }}
        />
      )}
    </>
  );
};

export default EditBrandInfo;
